'use client';

import { errorBrandConfig } from '@/lib/error/brandConfig';
import { BRAND } from '@/lib/cms/config';
import { captureError } from '@/lib/errors';
import { devError } from '@/lib/utils/devLog';
import { useEffect } from 'react';

type ErrorStrings = {
  title: string;
  subtitle: string;
  description: string;
  backToBrand: string;
  pageTitle: string;
};

const T: Record<string, ErrorStrings> = {
  en: {
    title: 'Sorry',
    subtitle: 'An unexpected error happened.',
    description: 'You can contact us by email {email} or go back to {website}.',
    backToBrand: 'Back to {brand}',
    pageTitle: 'Error | {brand}',
  },
  fr: {
    title: 'Désolé',
    subtitle: "Une erreur inattendue s'est produite.",
    description: 'Vous pouvez nous contacter par e-mail à {email} ou revenir sur {website}.',
    backToBrand: 'Retour à {brand}',
    pageTitle: 'Erreur | {brand}',
  },
  de: {
    title: 'Entschuldigung',
    subtitle: 'Ein unerwarteter Fehler ist aufgetreten.',
    description:
      'Sie können uns per E-Mail unter {email} kontaktieren oder zu {website} zurückkehren.',
    backToBrand: 'Zurück zu {brand}',
    pageTitle: 'Fehler | {brand}',
  },
  es: {
    title: 'Lo sentimos',
    subtitle: 'Se ha producido un error inesperado.',
    description: 'Puede contactarnos por correo electrónico en {email} o volver a {website}.',
    backToBrand: 'Volver a {brand}',
    pageTitle: 'Error | {brand}',
  },
  it: {
    title: 'Ci scusiamo',
    subtitle: 'Si è verificato un errore imprevisto.',
    description: "Puoi contattarci via e-mail all'indirizzo {email} o tornare a {website}.",
    backToBrand: 'Torna a {brand}',
    pageTitle: 'Errore | {brand}',
  },
  pt: {
    title: 'Desculpe',
    subtitle: 'Ocorreu um erro inesperado.',
    description: 'Pode contactar-nos por e-mail em {email} ou voltar a {website}.',
    backToBrand: 'Voltar a {brand}',
    pageTitle: 'Erro | {brand}',
  },
  ru: {
    title: 'Извините',
    subtitle: 'Произошла непредвиденная ошибка.',
    description:
      'Вы можете связаться с нами по электронной почте {email} или вернуться на {website}.',
    backToBrand: 'Вернуться на {brand}',
    pageTitle: 'Ошибка | {brand}',
  },
  ja: {
    title: '申し訳ございません',
    subtitle: '予期しないエラーが発生しました。',
    description: '{email} までメールでお問い合わせいただくか、{website} にお戻りください。',
    backToBrand: '{brand}に戻る',
    pageTitle: 'エラー | {brand}',
  },
  ko: {
    title: '죄송합니다',
    subtitle: '예기치 않은 오류가 발생했습니다.',
    description: '{email}으로 이메일을 보내시거나 {website}으로 돌아가실 수 있습니다.',
    backToBrand: '{brand}(으)로 돌아가기',
    pageTitle: '오류 | {brand}',
  },
  zh: {
    title: '抱歉',
    subtitle: '发生了意外错误。',
    description: '您可以通过电子邮件 {email} 联系我们，或返回 {website}。',
    backToBrand: '返回{brand}',
    pageTitle: '错误 | {brand}',
  },
  zht: {
    title: '抱歉',
    subtitle: '發生了意外錯誤。',
    description: '您可以透過電子郵件 {email} 聯繫我們，或返回 {website}。',
    backToBrand: '返回{brand}',
    pageTitle: '錯誤 | {brand}',
  },
};

const SLUG_MAP: Record<string, string> = { jp: 'ja' };

function detectLang(): string {
  if (typeof window === 'undefined') return 'en';
  const match = window.location.pathname.match(/^\/[a-z]{2}-([a-z]{2,3})\//);
  if (!match) return 'en';
  const slug = match[1];
  return SLUG_MAP[slug] ?? slug;
}

function interpolate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? '');
}

export default function GlobalError({ error }: { error: Error & { digest?: string } }) {
  const config = errorBrandConfig;
  const lang = detectLang();
  const t = T[lang] ?? T.en;
  const year = new Date().getFullYear();

  useEffect(() => {
    document.title = interpolate(t.pageTitle, { brand: config.name });
    devError('[Global Error]', error.message, error.digest);
    captureError(error, { tags: { source: 'global-error' } });
  }, [error, t.pageTitle, config.name]);

  return (
    <html lang={lang} data-brand={BRAND}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content="noindex, nofollow" />
        {BRAND === 'gallet' && (
          <link
            rel="preload"
            href="/fonts/moderat/Moderat-Regular.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
        )}
        <style
          dangerouslySetInnerHTML={{
            __html: `a:focus-visible { outline: 2px solid ${errorBrandConfig.bgColor}; outline-offset: 2px; }`,
          }}
        />
      </head>
      <body style={{ margin: 0, fontFamily: "'Moderat', system-ui, sans-serif" }}>
        {/* Logo bar */}
        <div
          style={{
            backgroundColor: config.bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px 24px',
          }}
        >
          <img src={config.logoSrc} alt={config.name} height={52} />
        </div>

        {/* Error content */}
        <main
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            minHeight: 'calc(100vh - 130px)',
            padding: '64px 24px',
          }}
        >
          <h1
            style={{
              fontFamily: "'Moderat Extended', 'Moderat', system-ui, sans-serif",
              fontSize: 'clamp(28px, 5vw, 48px)',
              fontWeight: 500,
              lineHeight: 1.1,
              textTransform: 'uppercase',
              margin: '0 0 8px',
              color: '#1a1a1a',
            }}
          >
            {t.title}
          </h1>
          <h2
            style={{
              fontFamily: "'Moderat Extended', 'Moderat', system-ui, sans-serif",
              fontSize: 'clamp(28px, 5vw, 48px)',
              fontWeight: 500,
              lineHeight: 1.1,
              textTransform: 'uppercase',
              margin: '0 0 24px',
              color: '#1a1a1a',
            }}
          >
            {t.subtitle}
          </h2>
          <p
            style={{
              fontSize: '16px',
              color: '#666',
              margin: '0 0 32px',
              maxWidth: '600px',
            }}
          >
            {interpolate(t.description, {
              email: config.email,
              website: config.website,
            })}
          </p>
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- global-error replaces the entire document; Next.js router is unavailable */}
          <a
            href="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '48px',
              padding: '12px 24px',
              border: `2px solid ${config.bgColor}`,
              backgroundColor: 'transparent',
              color: config.bgColor,
              fontFamily: "'Moderat', system-ui, sans-serif",
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '0',
              textTransform: 'uppercase',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            {interpolate(t.backToBrand, { brand: config.name })}
          </a>
        </main>

        {/* Copyright footer */}
        <footer
          style={{
            backgroundColor: config.bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px 24px',
          }}
        >
          <p
            style={{
              color: 'white',
              fontSize: '14px',
              margin: 0,
              fontFamily: "'Moderat', system-ui, sans-serif",
            }}
          >
            &copy;{year} {config.name}, All rights reserved.
          </p>
        </footer>
      </body>
    </html>
  );
}
